import React, { useState, useEffect } from 'react';
import { 
  Flame, 
  Plus, 
  Search, 
  Filter,
  Edit3,
  Trash2,
  Eye,
  Star,
  MapPin,
  User,
  IndianRupee,
  Clock,
  Calendar,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Download,
  MoreHorizontal
} from 'lucide-react';
import apiService from '../services/api';
import AddEditServiceModal from '../components/modals/AddEditServiceModal';
import AddEditCategoryModal from '../components/modals/AddEditCategoryModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import { toast } from 'react-hot-toast';

function Services() {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedService, setSelectedService] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [editingService, setEditingService] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  
  // Category management states
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showCategoryDeleteModal, setShowCategoryDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categorySaveLoading, setCategorySaveLoading] = useState(false);
  const [categoryDeleteLoading, setCategoryDeleteLoading] = useState(false);
  const [showCategoryManagement, setShowCategoryManagement] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    categories: 0
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });

  useEffect(() => {
    fetchServices();
    fetchCategories();
  }, []);

  const fetchServices = async (page = 1, append = false) => {
    try {
      if (!append) {
        setLoading(true);
      } else {
        setLoadMoreLoading(true);
      }
      setError('');
      
      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString()
      });
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      if (categoryFilter && categoryFilter !== 'all') {
        params.append('categoryId', categoryFilter);
      }
      if (statusFilter && statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      const response = await apiService.getAllAdminServices(params);
      
      if (response.success) {
        const newServices = response.data || [];
        
        if (append) {
          // Append new services to existing ones
          setServices(prevServices => [...prevServices, ...newServices]);
        } else {
          // Replace services (for initial load or filter changes)
          setServices(newServices);
        }
        
        setPagination(response.pagination || pagination);
        if (!append) {
          // Only update stats on initial load, not on load more
          setStats(response.stats || stats);
        }
      } else {
        throw new Error(response.message || 'Failed to fetch services');
      }
    } catch (err) {
      console.error('Error fetching services:', err);
      setError(err.message || 'Failed to fetch services');
      toast.error('Failed to fetch services');
      
      if (!append) {
        // Fallback to empty state only on initial load
        setServices([]);
        setStats({ total: 0, active: 0, inactive: 0, categories: 0 });
      }
    } finally {
      if (!append) {
        setLoading(false);
      } else {
        setLoadMoreLoading(false);
      }
    }
  };

  const fetchCategories = async (includeInactive = false) => {
    try {
      const params = new URLSearchParams();
      if (includeInactive) {
        params.append('includeInactive', 'true');
      }
      
      const response = await apiService.getAllAdminCategories(params);
      
      if (response.success && response.data) {
        setCategories(response.data);
      } else {
        // Fallback to legacy API
        const legacyResponse = await apiService.getAllServicesCategories();
        if (legacyResponse.success && legacyResponse.data) {
          setCategories(legacyResponse.data.filter(cat => cat.isActive));
        } else {
          throw new Error('Failed to fetch categories');
        }
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
      // Use fallback categories on error
      const fallbackCategories = [
        { id: 'cat_1', name: 'Puja Ceremonies', isActive: true, serviceCount: 0 },
        { id: 'cat_2', name: 'Astrology', isActive: true, serviceCount: 0 },
        { id: 'cat_3', name: 'Vedic Rituals', isActive: true, serviceCount: 0 }
      ];
      setCategories(fallbackCategories);
    }
  };

  // Category CRUD Handler Functions
  const handleCreateCategory = () => {
    setEditingCategory(null);
    setShowCategoryModal(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setShowCategoryModal(true);
  };

  const handleDeleteCategoryClick = (category) => {
    setCategoryToDelete(category);
    setShowCategoryDeleteModal(true);
  };

  const handleSaveCategory = async (categoryData) => {
    try {
      setCategorySaveLoading(true);
      
      let response;
      if (editingCategory) {
        // Update existing category
        response = await apiService.updateAdminCategory(editingCategory.id, categoryData);
        if (response.success) {
          toast.success('Category updated successfully!');
        }
      } else {
        // Create new category
        response = await apiService.createAdminCategory(categoryData);
        if (response.success) {
          toast.success('Category created successfully!');
        }
      }
      
      if (response.success) {
        setShowCategoryModal(false);
        setEditingCategory(null);
        await fetchCategories(true); // Refresh categories
        await fetchServices(pagination.page); // Refresh services
      } else {
        throw new Error(response.message || 'Failed to save category');
      }
    } catch (err) {
      console.error('Error saving category:', err);
      toast.error(err.message || 'Failed to save category');
    } finally {
      setCategorySaveLoading(false);
    }
  };

  const handleDeleteCategoryConfirm = async () => {
    if (!categoryToDelete) return;
    
    try {
      setCategoryDeleteLoading(true);
      const response = await apiService.deleteAdminCategory(categoryToDelete.id);
      
      if (response.success) {
        toast.success('Category deleted successfully!');
        setShowCategoryDeleteModal(false);
        setCategoryToDelete(null);
        await fetchCategories(true); // Refresh categories
        await fetchServices(pagination.page); // Refresh services
      } else {
        throw new Error(response.message || 'Failed to delete category');
      }
    } catch (err) {
      console.error('Error deleting category:', err);
      toast.error(err.message || 'Failed to delete category');
    } finally {
      setCategoryDeleteLoading(false);
    }
  };

  // CRUD Handler Functions
  const handleCreateService = () => {
    setEditingService(null);
    setShowAddEditModal(true);
  };

  const handleEditService = (service) => {
    setEditingService(service);
    setShowAddEditModal(true);
  };

  const handleDeleteClick = (service) => {
    setServiceToDelete(service);
    setShowDeleteModal(true);
  };

  const handleSaveService = async (serviceData) => {
    try {
      setSaveLoading(true);
      
      let response;
      if (editingService) {
        // Update existing service
        response = await apiService.updateAdminService(editingService.id, serviceData);
        if (response.success) {
          toast.success('Service updated successfully!');
        }
      } else {
        // Create new service
        response = await apiService.createAdminService(serviceData);
        if (response.success) {
          toast.success('Service created successfully!');
        }
      }
      
      if (response.success) {
        setShowAddEditModal(false);
        setEditingService(null);
        await fetchServices(pagination.page);
      } else {
        throw new Error(response.message || 'Failed to save service');
      }
    } catch (err) {
      console.error('Error saving service:', err);
      toast.error(err.message || 'Failed to save service');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!serviceToDelete) return;
    
    try {
      setDeleteLoading(true);
      const response = await apiService.deleteAdminService(serviceToDelete.id);
      
      if (response.success) {
        toast.success('Service deleted successfully!');
        setShowDeleteModal(false);
        setServiceToDelete(null);
        await fetchServices(pagination.page);
      } else {
        throw new Error(response.message || 'Failed to delete service');
      }
    } catch (err) {
      console.error('Error deleting service:', err);
      toast.error(err.message || 'Failed to delete service');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleStatusToggle = async (service) => {
    try {
      const newStatus = !service.active;
      const response = await apiService.updateAdminServiceStatus(service.id, newStatus);
      
      if (response.success) {
        toast.success(`Service ${newStatus ? 'activated' : 'deactivated'} successfully!`);
        await fetchServices(pagination.page);
      } else {
        throw new Error(response.message || 'Failed to update service status');
      }
    } catch (err) {
      console.error('Error updating service status:', err);
      toast.error(err.message || 'Failed to update service status');
    }
  };

  // Handle Load More functionality
  const handleLoadMore = async () => {
    if (pagination.hasNext && !loadMoreLoading) {
      const nextPage = pagination.page + 1;
      await fetchServices(nextPage, true); // true for append mode
    }
  };

  // Filter and search effects
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchServices(1); // Reset to page 1 when filters change
    }, 500);
    
    return () => clearTimeout(timeoutId);
  }, [searchTerm, categoryFilter, statusFilter]);

  const filteredServices = services; // Services are already filtered by API

  const ServiceDetailsModal = ({ service, onClose }) => {
    if (!service) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Service Details</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Service Info */}
            <div className="bg-orange-50 p-4 rounded-lg">
              <h3 className="font-semibold text-orange-800 mb-2">{service.title}</h3>
              <p className="text-gray-600 mb-3">{service.description}</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Price</p>
                  <p className="font-medium flex items-center">
                    <IndianRupee className="w-4 h-4 mr-1" />
                    {service.basePrice}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Duration</p>
                  <p className="font-medium flex items-center">
                    <Clock className="w-4 h-4 mr-1" />
                    {service.durationMin}
                  </p>
                </div>
              </div>
            </div>

            {/* Category */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="font-semibold text-blue-800 mb-2">Category</h3>
              <p className="text-gray-600">{service.category?.name}</p>
            </div>

            {/* Available Pandits */}
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-green-800">Available Pandits</h3>
                <span className="text-sm text-green-700">
                  {service.availablePandits || service.panditExpertise?.length || 0} total
                  {service.verifiedPandits && (
                    <span className="text-green-800 ml-1 font-medium">
                      ({service.verifiedPandits} verified)
                    </span>
                  )}
                </span>
              </div>
              
              {service.panditExpertise?.length > 0 ? (
                <div className="space-y-3">
                  {service.panditExpertise.map((expertise, index) => (
                    <div key={index} className="bg-white p-3 rounded-lg border border-green-200">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center">
                          {expertise.pandit?.profile?.image ? (
                            <img 
                              src={expertise.pandit.profile.image} 
                              alt={expertise.pandit?.profile?.name}
                              className="w-10 h-10 rounded-full object-cover mr-3"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-medium mr-3">
                              {expertise.pandit?.profile?.name?.charAt(0) || 'P'}
                            </div>
                          )}
                          <div>
                            <h4 className="font-medium text-gray-900">
                              {expertise.pandit?.profile?.name || 'Pandit'}
                            </h4>
                            <p className="text-sm text-gray-600">
                              {expertise.pandit?.profile?.city}, {expertise.pandit?.profile?.state}
                            </p>
                            {expertise.pandit?.profile?.experience && (
                              <p className="text-xs text-gray-500">
                                {expertise.pandit.profile.experience} years experience
                              </p>
                            )}
                            {expertise.pandit?.profile?.languages?.length > 0 && (
                              <p className="text-xs text-blue-600">
                                Languages: {expertise.pandit.profile.languages.join(', ')}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <div className="text-right">
                          {expertise.pandit?.pricing?.price ? (
                            <div>
                              <p className="font-semibold text-green-600 text-lg">
                                ₹{expertise.pandit.pricing.price}
                              </p>
                              {expertise.pandit.pricing.durationMin && (
                                <p className="text-xs text-gray-500">
                                  {expertise.pandit.pricing.durationMin} minutes
                                </p>
                              )}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500">
                              Base: ₹{service.basePrice}
                            </p>
                          )}
                          
                          {expertise.pandit?.profile?.ratingAvg && (
                            <div className="flex items-center mt-1">
                              <Star className="w-4 h-4 text-yellow-400 fill-current" />
                              <span className="ml-1 text-sm text-gray-600">
                                {expertise.pandit.profile.ratingAvg.toFixed(1)}
                                {expertise.pandit.profile.ratingCount && (
                                  <span className="text-xs text-gray-400 ml-1">
                                    ({expertise.pandit.profile.ratingCount})
                                  </span>
                                )}
                              </span>
                            </div>
                          )}
                          
                          <div className="flex items-center mt-1">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              expertise.pandit?.user?.status === 'ACTIVE' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {expertise.pandit?.user?.status === 'ACTIVE' ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {expertise.pandit?.profile?.bio && (
                        <div className="mt-2 pt-2 border-t border-green-100">
                          <p className="text-sm text-gray-600">{expertise.pandit.profile.bio}</p>
                        </div>
                      )}
                      
                      {expertise.pandit?.pricing?.notes && (
                        <div className="mt-2 pt-2 border-t border-green-100">
                          <p className="text-xs text-gray-500">
                            <strong>Note:</strong> {expertise.pandit.pricing.notes}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <User className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500">No pandits available for this service</p>
                </div>
              )}
            </div>

            {/* Status */}
            <div className="flex items-center justify-between">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                service.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {service.active ? (
                  <CheckCircle className="w-4 h-4 mr-2" />
                ) : (
                  <XCircle className="w-4 h-4 mr-2" />
                )}
                {service.active ? 'Active' : 'Inactive'}
              </span>
              <div className="text-sm text-gray-500">
                Created: {new Date(service.createdAt).toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            Spiritual Services
          </h1>
          <p className="mt-2 text-gray-600">
            Manage all spiritual services, rituals, and ceremonies
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex flex-wrap gap-3">
          <button
            onClick={() => fetchServices(pagination.page)}
            className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors duration-200"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
          <button className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors duration-200">
            <Download className="w-4 h-4 mr-2" />
            Export
          </button>
          <button
            onClick={handleCreateCategory}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            <Filter className="w-4 h-4 mr-2" />
            Add Category
          </button>
          <button
            onClick={handleCreateService}
            className="inline-flex items-center px-4 py-2 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 transition-colors duration-200"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Service
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Flame className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Services</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Services</p>
              <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <XCircle className="w-6 h-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Inactive Services</p>
              <p className="text-2xl font-bold text-gray-900">{stats.inactive}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Filter className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Categories</p>
              <p className="text-2xl font-bold text-gray-900">{stats.categories}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search services..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="all">All Categories</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setCategoryFilter('all');
                setStatusFilter('all');
              }}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Category Management Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Categories</h2>
          <button
            onClick={() => setShowCategoryManagement(!showCategoryManagement)}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            {showCategoryManagement ? 'Hide Categories' : 'Manage Categories'}
          </button>
        </div>
        
        {showCategoryManagement && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((category) => (
              <div key={category.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center">
                    {category.iconUrl ? (
                      <img 
                        src={category.iconUrl} 
                        alt={category.name}
                        className="w-6 h-6 rounded object-cover mr-2"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <Filter className="w-6 h-6 text-blue-500 mr-2" />
                    )}
                    <div>
                      <h3 className="font-medium text-gray-900">{category.name}</h3>
                      {category.parent && (
                        <p className="text-xs text-gray-500">Under: {category.parent.name}</p>
                      )}
                    </div>
                  </div>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    category.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {category.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">{category.serviceCount || 0}</span> services
                    {category.children && category.children.length > 0 && (
                      <span className="ml-2">
                        • <span className="font-medium">{category.children.length}</span> subcategories
                      </span>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditCategory(category)}
                      className="p-1 text-gray-400 hover:text-blue-600"
                      title="Edit Category"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteCategoryClick(category)}
                      className="p-1 text-gray-400 hover:text-red-600"
                      title="Delete Category"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Add Category Card */}
            <div 
              onClick={handleCreateCategory}
              className="bg-blue-50 border-2 border-dashed border-blue-300 rounded-lg p-4 flex flex-col items-center justify-center cursor-pointer hover:bg-blue-100 transition-colors duration-200"
            >
              <Plus className="w-8 h-8 text-blue-500 mb-2" />
              <span className="text-blue-600 font-medium">Add Category</span>
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <XCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Services Grid */}
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredServices.length > 0 ? (
            filteredServices.map((service) => (
            <div key={service.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <Flame className="w-5 h-5 text-orange-600" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-lg font-semibold text-gray-900">{service.title}</h3>
                      <p className="text-sm text-gray-500">{service.category?.name}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleStatusToggle(service)}
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium transition-colors duration-200 hover:opacity-80 ${
                      service.active ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-red-100 text-red-800 hover:bg-red-200'
                    }`}
                    title={`Click to ${service.active ? 'deactivate' : 'activate'} service`}
                  >
                    {service.active ? 'Active' : 'Inactive'}
                  </button>
                </div>

                <p className="text-gray-600 text-sm mb-4 line-clamp-2">{service.description}</p>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-gray-500">Price</p>
                    <p className="font-semibold text-gray-900 flex items-center">
                      <IndianRupee className="w-4 h-4 mr-1" />
                      {service.basePrice}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Duration</p>
                    <p className="font-semibold text-gray-900 flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {service.durationMin ? `${service.durationMin} min` : service.duration || 'N/A'}
                    </p>
                  </div>
                </div>

                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-gray-500">Available Pandits</p>
                    <span className="text-xs text-blue-600 font-medium">
                      {service.availablePandits || service.panditExpertise?.length || 0} total
                      {service.verifiedPandits && (
                        <span className="text-green-600 ml-1">
                          ({service.verifiedPandits} verified)
                        </span>
                      )}
                    </span>
                  </div>
                  
                  {service.panditExpertise?.length > 0 ? (
                    <div className="space-y-2">
                      {service.panditExpertise.slice(0, 2).map((expertise, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded text-xs">
                          <div className="flex items-center">
                            {expertise.pandit?.profile?.image ? (
                              <img 
                                src={expertise.pandit.profile.image} 
                                alt={expertise.pandit?.profile?.name}
                                className="w-6 h-6 rounded-full object-cover mr-2"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center text-white text-xs font-medium mr-2">
                                {expertise.pandit?.profile?.name?.charAt(0) || 'P'}
                              </div>
                            )}
                            <div>
                              <p className="font-medium text-gray-900">
                                {expertise.pandit?.profile?.name || 'Pandit'}
                              </p>
                              <p className="text-gray-500">
                                {expertise.pandit?.profile?.city}, {expertise.pandit?.profile?.state}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            {expertise.pandit?.pricing?.price && (
                              <p className="font-medium text-green-600">
                                ₹{expertise.pandit.pricing.price}
                              </p>
                            )}
                            {expertise.pandit?.profile?.ratingAvg && (
                              <div className="flex items-center">
                                <Star className="w-3 h-3 text-yellow-400 fill-current" />
                                <span className="ml-1 text-gray-600">
                                  {expertise.pandit.profile.ratingAvg.toFixed(1)}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      
                      {service.panditExpertise.length > 2 && (
                        <div className="text-center">
                          <button className="text-xs text-blue-600 hover:text-blue-800">
                            +{service.panditExpertise.length - 2} more pandits
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-2">
                      <p className="text-xs text-gray-400">No pandits available</p>
                    </div>
                  )}
                </div>

                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      setSelectedService(service);
                      setShowDetails(true);
                    }}
                    className="flex-1 bg-orange-600 text-white py-2 px-3 rounded-lg hover:bg-orange-700 transition-colors duration-200 text-sm font-medium flex items-center justify-center"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View Details
                  </button>
                  <button 
                    onClick={() => handleEditService(service)}
                    className="p-2 text-gray-400 hover:text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                    title="Edit Service"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={() => handleDeleteClick(service)}
                    className="p-2 text-red-400 hover:text-red-600 border border-gray-300 rounded-lg hover:bg-red-50"
                    title="Delete Service"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full text-center py-12">
            <Flame className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No services found</h3>
            <p className="mt-1 text-sm text-gray-500">
              No services match your current filters.
            </p>
            <div className="mt-6">
              <button
                onClick={handleCreateService}
                className="inline-flex items-center px-4 py-2 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 transition-colors duration-200"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add First Service
              </button>
            </div>
          </div>
        )}
        </div>

        {/* Load More Button */}
        {pagination.hasNext && filteredServices.length > 0 && (
          <div className="flex justify-center">
            <button
              onClick={handleLoadMore}
              disabled={loadMoreLoading}
              className="inline-flex items-center px-6 py-3 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 disabled:bg-orange-400 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {loadMoreLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Loading More...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Load More Services ({pagination.total - filteredServices.length} remaining)
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Service Details Modal */}
      {showDetails && (
        <ServiceDetailsModal
          service={selectedService}
          onClose={() => setShowDetails(false)}
        />
      )}

      {/* Add/Edit Service Modal */}
      <AddEditServiceModal
        isOpen={showAddEditModal}
        onClose={() => {
          setShowAddEditModal(false);
          setEditingService(null);
        }}
        onSave={handleSaveService}
        service={editingService}
        categories={categories}
        loading={saveLoading}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setServiceToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Service"
        message="Are you sure you want to delete this service? This action cannot be undone."
        itemName={serviceToDelete?.title}
        loading={deleteLoading}
        danger={true}
      />

      {/* Add/Edit Category Modal */}
      <AddEditCategoryModal
        isOpen={showCategoryModal}
        onClose={() => {
          setShowCategoryModal(false);
          setEditingCategory(null);
        }}
        onSave={handleSaveCategory}
        category={editingCategory}
        categories={categories}
        loading={categorySaveLoading}
      />

      {/* Delete Category Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showCategoryDeleteModal}
        onClose={() => {
          setShowCategoryDeleteModal(false);
          setCategoryToDelete(null);
        }}
        onConfirm={handleDeleteCategoryConfirm}
        title="Delete Category"
        message={`Are you sure you want to delete this category? ${categoryToDelete?.serviceCount > 0 ? 'This will affect all services in this category.' : 'This action cannot be undone.'}`}
        itemName={categoryToDelete?.name}
        loading={categoryDeleteLoading}
        danger={true}
      />
    </div>
  );
}

export default Services;